// Stop the type system complaining about redux devtools,
// which is attached to the Window by chrome/ff extension "redux-devtools".
declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: any;
    Cypress: any;
    store: any;
  }
}

export interface NavLinkProps {
  to: string;
  children: React.ReactNode;
}

export enum HTTPMethod {
  GET = "GET",
  PUT = "PUT",
  POST = "POST",
  HEAD = "HEAD",
  DELETE = "DELETE",
}

export interface ApexApiError extends Error {
  response?: Response;
  status?: number;
}

export interface menuItem {
  headerText: string;
  parentHeader: string;
  urlText: string;
  webLinkId: string;
}

export interface APIResponse {
  code: string;
  message: string;
}

export interface PortfolioEntry {
  securityType: string;
  cusip: string;
  tkr: string;
  curAmt: string;
  amtChg: string;
  reqChg: string;
}

export interface VarPortfolioEntry {
  securityType: string;
  Symbol: string;
  CUSIP: string;
  StartAmount: string;
  CurrentAmount: string;
  AmountChange: string;
  RequirementChange: string;
}

export interface Report {
  sliceID: string;
  businessDate: string;
  portfolio: PortfolioEntry[];
  apiResponse: APIResponse;
}

export interface CFDetail {
  "coverage-requirement-amount": ReportValues;
  "non-cns-requirement-amount": ReportValues;
  "mrd-requirement-amount": ReportValues;
  "illiquid-requirement-amount": ReportValues;
  "wi-mtm-requirement-amount": ReportValues;
  "fund-serv-requirement-amount": ReportValues;
  "spc-requirement-amount": ReportValues;
  "minimum-charge-amount": ReportValues;
  "cns-fails-requirement-amount": ReportValues;
  "mtm-requirement-amount": ReportValues;
  "var-requirement-amount": ReportValues;
}

export interface ReportValues {
  previous: string;
  "%change": string;
  current: string;
}

export interface ProjectedTotal {
  var: {
    sliceId: string;
    "var-requirement-amount": ReportValues;
  };
  mtm: {
    "mtm-requirement-amount": ReportValues;
    sliceId: string;
  };
  total: {
    excess: ReportValues;
    total: ReportValues;
  };
}

export interface CFTotal {
  total: ReportValues;
  deposit: ReportValues;
  excess: ReportValues;
}

export interface AlertItem {
  alertid: number;
  risk_type: string;
  requirement_value: number;
  threshold_value: number;
  sliceid: number;
  created_at: string;
}

export interface Slice {
  id: string;
  date: string;
}

export interface CFItem {
  projected?: ProjectedTotal;
  total: CFTotal;
  curSlice: Slice;
  detail: CFDetail;
  version: string;
  startSlice: Slice;
  trend?: TrendInfo;
  "apex-trend"?: TrendInfo;
}

export type ChartValue = { slice: string; value: string };

export interface TrendInfo {
  mtm?: TrendInfo;
  totalRequirement: {
    min: string;
    values: any;
    max: string;
  };
  var?: TrendInfo;
}

export interface VarItem {
  totalStartReq: string;
  totalCurPosition: string;
  totalStartPosition: string;
  portfolio: any;
  trend?: TrendInfo;
  curSlice: Slice;
  totalCurReq: string;
  totalPositionChange: string;
  version: string;
  startSlice: Slice;
}

export interface MtmItem {
  totalStartReq: string;
  portfolioDiff: PortfolioEntry[];
  trend?: TrendInfo;
  totalReqChg: string;
  curSlice: Slice;
  totalCurReq: string;
  version: string;
  startSlice: Slice;
}

export interface InstrumentItem {
  "mtm-requirement"?: InstrumentValue;
  "var-requirement"?: InstrumentValue;
  "apex-var-requirement"?: InstrumentValue;
  "mtm-position-quantity"?: InstrumentValue;
  "mtm-position-amount"?: InstrumentValue;
  "var-position-quantity"?: InstrumentValue;
  "var-position-amount"?: InstrumentValue;
  "apex-var-position-quantity"?: InstrumentValue;
  "apex-var-position-amount"?: InstrumentValue;
}

export interface InstrumentValue {
  values: any;
  low: number;
  high: number;
}

export type InstrumentParams = {
  tkrCusip: TkrCusip;
  date: string;
  slice: string;
};

export type CorrespondentParams = {
  tkrCusip: TkrCusip;
  date: string;
  slice: string;
};

export interface InstrumentType {
  reportType: string;
  date: string;
  slice: string;
  value: string;
  change: string;
  changePercentage: string;
  range: {
    high: number;
    low: number;
  };
}

export interface SummaryReport {
  sliceId: string;
  reportValues: ReportValues;
}

export interface TkrCusip {
  tkr: string;
  cusip: string;
}

export interface CorrespondentItem {
  correspondent: Correspondent;
}

export interface Correspondent {
  totalMovement: number;
  executionDataList: CorrespondentInfo[];
}

export interface CorrespondentInfo {
  correspondentCode: string;
  side: string;
  symbol: string;
  qty: number;
}

export interface DetailedError {
  details: any;
  error: boolean;
}
